import { useContext, useMemo } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useIsLandscape } from '@/hooks/useIsLandscape';

export const useTemplateFeatures = () => {
  const {
    viewer: { screen },
    template,
  } = useContext(CheckoutContext);

  const isMobile = useIsMobile();
  const landscape = useIsLandscape();

  const shouldShowExpressCheckout = useMemo(
    () => template.features.showExpressCheckout,
    [template.features.showExpressCheckout]
  );

  const shouldShowPaymentMethods = useMemo(
    () => template.features.showPaymentMethods,
    [template.features.showPaymentMethods]
  );

  // The landscape phone band (700 < w <= 1023, h <= 500) is `!isMobile` but
  // routes sub-screen clicks (add/change card, other methods) through the
  // viewer — not the portrait drawer. Templates that disable the desktop
  // viewer (e.g. PREFERRED) therefore set `viewer.screen` with no host to
  // render it, leaving a blank panel on the first "add new card" tap. Mount
  // the viewer host in landscape whenever a screen is actually open so the
  // selected sub-screen renders, mirroring how the desktop/DEFAULT path works.
  // Templates that already enable the viewer keep their existing behavior, and
  // portrait/true-desktop are unaffected (`isLandscape()` is false there). Templates
  // with `inlineSubScreens` (EMBEDDED_VIEW, EMBEDDED_PREFERRED_PAYMENT_METHOD)
  // host their sub-screens in this viewer on phones too — that's the whole
  // point of the flag — so the host must mount there even though `isMobile` is true.
  const inlineScreens = template.features.inlineSubScreens;

  const shouldShowDesktopViewer = useMemo(
    () => (!isMobile && template.features.showDesktopViewer) || ((landscape || inlineScreens) && !!screen),
    [isMobile, template.features.showDesktopViewer, screen, landscape, inlineScreens]
  );

  // An in-place sub-screen REPLACES the payment form, where the drawer merely
  // covers it — so the form has to go while one is open, on mobile too when the
  // template renders screens inline.
  const shouldShowPaymentMethodForm = useMemo(() => {
    if (screen && (!isMobile || inlineScreens)) {
      return false;
    }
    return template.features.autoSelectPaymentMethod || isMobile || !screen;
  }, [template.features.autoSelectPaymentMethod, isMobile, screen, inlineScreens]);

  const centerContentOnDesktop = useMemo(
    () => template.features.centerContentOnDesktop,
    [template.features.centerContentOnDesktop]
  );

  return {
    isMobile,
    shouldShowExpressCheckout,
    shouldShowPaymentMethods,
    shouldShowDesktopViewer,
    shouldShowPaymentMethodForm,
    centerContentOnDesktop,
  };
};
